import React, { useState } from "react";
import {
  Alert,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import {
  createSalt,
  encryptPasscodeForHelpEscrow,
  encryptPasscodeWithRecovery,
  hashRecoveryPassword,
} from "../utils/screenTimeRecovery.js";
import { createSalt as createPasscodeSalt, hashPasscode } from "../utils/screenTimePasscode.js";

/**
 * After choosing a PIN, set up recovery email/password or skip.
 *
 * @param {{
 *   visible: boolean,
 *   pin: string,
 *   onCancel: () => void,
 *   onComplete: (patch: Record<string, unknown>) => Promise<void>|void,
 * }} props
 */
export function ScreenTimePasscodeSetup({ visible, pin, onCancel, onComplete }) {
  const [step, setStep] = useState("choose");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryPassword, setRecoveryPassword] = useState("");
  const [confirmRecoveryPassword, setConfirmRecoveryPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep("choose");
    setRecoveryEmail("");
    setRecoveryPassword("");
    setConfirmRecoveryPassword("");
    setBusy(false);
  };

  const finishSkip = async () => {
    setBusy(true);
    try {
      const salt = await createPasscodeSalt();
      const hash = await hashPasscode(pin, salt);
      const escrowSalt = await createSalt();
      const encEscrow = await encryptPasscodeForHelpEscrow(pin, escrowSalt);
      await onComplete({
        screenTimePasscodeHash: hash,
        screenTimePasscodeSalt: salt,
        screenTimePasscodeEncEscrow: encEscrow,
        screenTimeHelpEscrowSalt: escrowSalt,
        screenTimeRecoverySkipped: true,
        screenTimeRecoveryEmail: null,
        screenTimeRecoveryHash: null,
        screenTimeRecoverySalt: null,
        screenTimePasscodeEncRecovery: null,
      });
      reset();
    } finally {
      setBusy(false);
    }
  };

  const finishWithRecovery = async () => {
    const email = recoveryEmail.trim();
    const pw = recoveryPassword;
    const confirm = confirmRecoveryPassword;
    if (!email || !email.includes("@")) {
      Alert.alert("Email required", "Enter a valid recovery email.");
      return;
    }
    if (pw.length < 4) {
      Alert.alert("Too short", "Recovery password must be at least 4 characters.");
      return;
    }
    if (pw !== confirm) {
      Alert.alert("Mismatch", "Recovery passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const salt = await createPasscodeSalt();
      const hash = await hashPasscode(pin, salt);
      const escrowSalt = await createSalt();
      const encEscrow = await encryptPasscodeForHelpEscrow(pin, escrowSalt);
      const recoverySalt = await createSalt();
      const recoveryHash = await hashRecoveryPassword(pw, recoverySalt);
      const encRecovery = await encryptPasscodeWithRecovery(pin, pw, recoverySalt);
      await onComplete({
        screenTimePasscodeHash: hash,
        screenTimePasscodeSalt: salt,
        screenTimePasscodeEncEscrow: encEscrow,
        screenTimeHelpEscrowSalt: escrowSalt,
        screenTimeRecoverySkipped: false,
        screenTimeRecoveryEmail: email,
        screenTimeRecoveryHash: recoveryHash,
        screenTimeRecoverySalt: recoverySalt,
        screenTimePasscodeEncRecovery: encRecovery,
      });
      reset();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={() => {
        reset();
        onCancel();
      }}
    >
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === "ios" ? "padding" : undefined}>
        <ScrollView contentContainerStyle={styles.scroll}>
          <Text style={styles.title}>Recovery for your passcode</Text>
          <Text style={styles.subtitle}>
            Optional backup on this device only — not a cloud login. If you forget the passcode, you can recover with
            email + recovery password, or use Help to verify with your habit stats.
          </Text>

          {step === "choose" ? (
            <>
              <Pressable
                style={[styles.btn, styles.btnPrimary]}
                onPress={() => setStep("recovery")}
                disabled={busy}
              >
                <Text style={styles.btnText}>Set up recovery email</Text>
              </Pressable>
              <Pressable style={[styles.btn, styles.btnSkip]} onPress={() => void finishSkip()} disabled={busy}>
                <Text style={styles.btnTextDark}>Skip recovery</Text>
              </Pressable>
              <Text style={styles.skipNote}>
                Skipping is fine — you can still reveal the passcode later via Help using your habit name and best
                streak from Insights.
              </Text>
              <Pressable style={styles.cancel} onPress={() => { reset(); onCancel(); }} disabled={busy}>
                <Text style={styles.cancelText}>Cancel</Text>
              </Pressable>
            </>
          ) : (
            <>
              <Text style={styles.label}>Recovery email</Text>
              <TextInput
                style={styles.input}
                value={recoveryEmail}
                onChangeText={setRecoveryEmail}
                placeholder="you@example.com"
                autoCapitalize="none"
                keyboardType="email-address"
              />
              <Text style={styles.label}>Recovery password</Text>
              <TextInput
                style={styles.input}
                value={recoveryPassword}
                onChangeText={setRecoveryPassword}
                placeholder="Separate from your passcode"
                secureTextEntry
              />
              <Text style={styles.label}>Confirm recovery password</Text>
              <TextInput
                style={styles.input}
                value={confirmRecoveryPassword}
                onChangeText={setConfirmRecoveryPassword}
                placeholder="Confirm"
                secureTextEntry
              />
              <Pressable
                style={[styles.btn, styles.btnPrimary]}
                onPress={() => void finishWithRecovery()}
                disabled={busy}
              >
                <Text style={styles.btnText}>Save passcode + recovery</Text>
              </Pressable>
              <Pressable style={styles.cancel} onPress={() => setStep("choose")} disabled={busy}>
                <Text style={styles.cancelText}>Back</Text>
              </Pressable>
            </>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1, backgroundColor: "#fff" },
  scroll: { padding: 20, paddingTop: Platform.OS === "ios" ? 56 : 28 },
  title: { fontSize: 22, fontWeight: "800", color: "#111", marginBottom: 8 },
  subtitle: { fontSize: 13, color: "#6B7280", lineHeight: 19, marginBottom: 20 },
  label: { fontSize: 13, fontWeight: "700", color: "#374151", marginBottom: 6, marginTop: 8 },
  input: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    backgroundColor: "#FAFAFA",
    marginBottom: 4,
  },
  btn: { paddingVertical: 14, borderRadius: 10, alignItems: "center", marginTop: 10 },
  btnPrimary: { backgroundColor: "#1ABC9C" },
  btnSkip: { backgroundColor: "#F3F4F6", borderWidth: 1, borderColor: "#E5E7EB" },
  btnText: { color: "#fff", fontWeight: "800" },
  btnTextDark: { color: "#111", fontWeight: "800" },
  skipNote: { fontSize: 12, color: "#6B7280", marginTop: 12, lineHeight: 17 },
  cancel: { marginTop: 16, alignItems: "center" },
  cancelText: { color: "#2563EB", fontWeight: "700" },
});
